<script setup>
import Layout from "@/Shared/Layout";
import Swal from "sweetalert2";
import CreateUser from "@/Shared/CreateUser";
import EditUser from "@/Shared/EditUser";
import {ref, computed, watch} from "vue";
import { usePage } from "@inertiajs/inertia-vue3";
import {useDebounceFn} from "@vueuse/core";

const props = defineProps({ people: Array });
const auth = computed(() => usePage().props.value.auth);

const totalActiveUsers = computed(() => {
    return props.people.data.filter((person) => person.active).length;
});

const showCreate = ref(false);
const showEdit = ref(false);
const selectedUser = ref({});

const submit = async (type = "updated") => {
    const title =
        type === "created"
            ? "User created successfully"
            : "User updated successfully";
    Swal.fire({
        title: "Success",
        text: title,
        icon: "success",
    }).then((result) => {
        if (result.isConfirmed) {
            window.location.reload();
        }
    });
};
const createNew = () => {
    showCreate.value = true;
};

const handleEditUser = (item) => {
    selectedUser.value = item;
    showEdit.value = true;
};

const close = () => {
    showCreate.value = false;
    showEdit.value = false;
    selectedUser.value = {};
};

const search = ref('');

const searchRequest = useDebounceFn((newValue) => {
    axios.get(`supports/filter/${newValue}`).then((response) => {
        props.people.data = response.data.data;
    });
}, 300)

watch(search, async (newValue, oldValue) => {
    await searchRequest(newValue);
});
</script>
<template>
    <Layout sub="supports" main="Users">
        <div class="px-4 sm:px-6 lg:px-8">
            <div class="sm:flex sm:items-center">
                <div class="sm:flex-auto">
                    <h1 class="text-xl font-semibold text-gray-900">
                        Supports
                    </h1>
                    <p class="mt-2 text-sm text-gray-700">{{totalActiveUsers}} active accounts</p>
                </div>
                <div class="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
                    <button
                        @click="createNew()"
                        type="button"
                        class="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
                    >
                        Add Support
                    </button>
                </div>
            </div>
            <div class="sm:flex sm:items-center">
                <div class="sm:flex-auto"></div>
                <div align="right" class="flex flex-col mt-5">
                    <input
                        v-model="search"
                        required
                        name="search"
                        id="search"
                        placeholder="Search by name or email"
                        class="h-10 px-2 w-full rounded mt-2 text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 dark:border-gray-700 dark:focus:border-indigo-600 focus:outline-none focus:border focus:border-indigo-700 border-gray-300 border shadow"
                        type="text"
                    />
                </div>
            </div>
            <div class="mt-8 flex flex-col">

                <div class="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div
                        class="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8"
                    >
                        <div
                            class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg"
                        >
                            <table class="min-w-full divide-y divide-gray-300">
                                <thead class="bg-gray-50">
                                    <tr>
                                        <th
                                            scope="col"
                                            class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                                        >
                                            Name
                                        </th>
                                        <th
                                            scope="col"
                                            class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                        >
                                            Title
                                        </th>
                                        <th
                                            scope="col"
                                            class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                        >
                                            Status
                                        </th>
                                        <th
                                            scope="col"
                                            class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                        >
                                            Role
                                        </th>
                                        <th
                                            scope="col"
                                            class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                        >
                                            Created
                                        </th>
                                        <th
                                            scope="col"
                                            class="relative py-3.5 pl-3 pr-4 sm:pr-6"
                                        >
                                            <span class="sr-only">Edit</span>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody
                                    class="divide-y divide-gray-200 bg-white"
                                >
                                    <tr
                                        v-for="person in props.people.data"
                                        :key="person.id"
                                    >
                                        <td
                                            class="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6"
                                        >
                                            <div class="flex items-center">
                                                <div
                                                    class="h-10 w-10 flex-shrink-0"
                                                >
                                                    <img
                                                        class="h-10 w-10 rounded-full"
                                                        :src="person.image"
                                                        alt=""
                                                    />
                                                </div>
                                                <div class="ml-4">
                                                    <div
                                                        class="font-medium text-gray-900"
                                                    >
                                                        {{ person.name }}
                                                    </div>
                                                    <div class="text-gray-500">
                                                        {{ person.email }}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td
                                            class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                                        >
                                            <div class="text-gray-900">
                                                {{ person.title }}
                                            </div>
                                        </td>
                                        <td
                                            class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                                        >
                                            <span
                                                v-if="person.active"
                                                class="inline-flex rounded-full bg-green-100 px-2 text-xs font-semibold leading-5 text-green-800"
                                                >{{person.status.label}}</span
                                            >
                                            <span
                                                v-if="!person.active"
                                                class="inline-flex rounded-full bg-gray-100 px-2 text-xs font-semibold leading-5 text-black"
                                                >{{person.status.label}}</span
                                            >
                                        </td>
                                        <td
                                            class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                                        >
                                            {{ person.role }}
                                        </td>
                                        <td
                                            class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                                        >
                                            {{ person.createdAt }}
                                        </td>
                                        <td
                                            class="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6"
                                        >
                                            <button
                                                @click="handleEditUser(person)"
                                                type="button"
                                                class="text-indigo-600 hover:text-indigo-900"
                                                >Edit<span class="sr-only"
                                                    >, {{ person.name }}</span
                                                ></button
                                            >
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Layout>

    <CreateUser
        role="support"
        @submit="submit"
        @close="close"
        :show="showCreate"
    />
    <EditUser
        v-if="showEdit"
        :item.sync="selectedUser"
        role="support"
        @submit="submit"
        @close="close"
    />
</template>
